import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { MessageCircle, X, Send, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSession = () => {
  let s = localStorage.getItem("aov_chat_session");
  if (!s) {
    s = (crypto.randomUUID && crypto.randomUUID()) || `s-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    localStorage.setItem("aov_chat_session", s);
  }
  return s;
};

const GREETING =
  "Hi! 👋 I'm the Alpha Omega Vitality assistant. Ask me about products, shipping, returns, or subscriptions — I'm here 24/7. How can I help?";
const QUICK = ["Shipping & delivery", "Returns & refunds", "Which product is right for me?", "Talk to a human"];

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [handoff, setHandoff] = useState(false);
  const [lead, setLead] = useState({ name: "", email: "", message: "" });
  const [leadSent, setLeadSent] = useState(false);
  const [leadErr, setLeadErr] = useState("");
  const sessionRef = useRef(getSession());
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, handoff, leadSent]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    if (msg === "Talk to a human") {
      setMessages((m) => [...m, { role: "user", content: msg }]);
      setHandoff(true);
      setInput("");
      return;
    }
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionRef.current, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
      if (data.handoff) setHandoff(true);
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: "assistant", content: "Sorry, I had trouble responding. Please email customersupport@alphaomegavitality.com." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    setLeadErr("");
    if (!lead.name.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(lead.email)) {
      setLeadErr("Please enter your name and a valid email.");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/chat/handoff`, {
        session_id: sessionRef.current,
        name: lead.name,
        email: lead.email,
        message: lead.message,
      });
      setLeadSent(true);
      setHandoff(false);
      setMessages((m) => [...m, { role: "assistant", content: data.message }]);
    } catch (e) {
      setLeadErr("Something went wrong. Please email customersupport@alphaomegavitality.com.");
    }
  };

  return (
    <>
      {/* Bubble */}
      <motion.button
        onClick={() => setOpen((o) => !o)}
        data-testid="chat-bubble"
        aria-label="Open chat"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 200, damping: 18 }}
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-24 right-4 z-[90] flex h-14 w-14 items-center justify-center rounded-full bg-[#1d231e] text-[#f4f1eb] shadow-2xl ring-1 ring-[#c79a3e]/40 sm:bottom-6 sm:right-6"
      >
        <AnimatePresence mode="wait" initial={false}>
          {open ? (
            <motion.span key="x" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}>
              <X size={24} />
            </motion.span>
          ) : (
            <motion.span key="c" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }} className="relative">
              <MessageCircle size={24} />
              <span className="absolute -right-1.5 -top-1.5 h-3 w-3 rounded-full bg-[#2f9e44] ring-2 ring-[#1d231e]" />
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
            className="fixed bottom-40 right-4 z-[90] flex w-[calc(100vw-2rem)] max-w-[380px] flex-col overflow-hidden rounded-3xl border border-[#1a1f1b]/10 bg-[#f4f1eb] shadow-2xl sm:bottom-24 sm:right-6"
            style={{ height: "min(560px, calc(100vh - 9rem))" }}
          >
            {/* Header */}
            <div className="flex items-center gap-3 bg-[#1d231e] px-5 py-4 text-[#f4f1eb]">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#c79a3e] text-[#1d231e]">
                <MessageCircle size={18} />
              </span>
              <div className="leading-tight">
                <div className="text-sm font-semibold">Alpha Omega Vitality</div>
                <div className="flex items-center gap-1.5 text-[11px] text-[#f4f1eb]/70">
                  <span className="h-2 w-2 rounded-full bg-[#2f9e44]" /> AI assistant · replies instantly
                </div>
              </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-msg-${m.role}`}
                    className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#1d231e] text-[#f4f1eb] rounded-br-md"
                        : "bg-white text-[#1a1f1b] border border-[#1a1f1b]/10 rounded-bl-md"
                    }`}
                  >
                    {m.content}
                  </div>
                </div>
              ))}

              {loading && (
                <div className="flex justify-start" data-testid="chat-typing">
                  <div className="flex gap-1 rounded-2xl rounded-bl-md border border-[#1a1f1b]/10 bg-white px-4 py-3">
                    {[0, 1, 2].map((d) => (
                      <motion.span
                        key={d}
                        className="h-2 w-2 rounded-full bg-[#c79a3e]"
                        animate={{ opacity: [0.3, 1, 0.3] }}
                        transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Quick replies (only before first user message) */}
              {messages.length === 1 && !loading && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {QUICK.map((q) => (
                    <button
                      key={q}
                      onClick={() => send(q)}
                      data-testid={`chat-quick-${q.split(" ")[0].toLowerCase()}`}
                      className="rounded-full border border-[#1a1f1b]/15 bg-white px-3 py-1.5 text-xs text-[#1a1f1b] transition-colors hover:border-[#c79a3e] hover:text-[#b34700]"
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* Handoff lead form */}
              {handoff && !leadSent && (
                <form onSubmit={submitLead} data-testid="chat-handoff-form" className="mt-2 space-y-2 rounded-2xl border border-[#c79a3e]/40 bg-[#faf3e8] p-3.5">
                  <div className="text-xs font-medium text-[#1a1f1b]">Leave your details and our team will follow up by email:</div>
                  <input
                    data-testid="handoff-name"
                    value={lead.name}
                    onChange={(e) => setLead({ ...lead, name: e.target.value })}
                    placeholder="Your name"
                    className="w-full rounded-lg border border-[#1a1f1b]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#c79a3e]"
                  />
                  <input
                    data-testid="handoff-email"
                    type="email"
                    value={lead.email}
                    onChange={(e) => setLead({ ...lead, email: e.target.value })}
                    placeholder="Your email"
                    className="w-full rounded-lg border border-[#1a1f1b]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#c79a3e]"
                  />
                  <textarea
                    data-testid="handoff-message"
                    value={lead.message}
                    onChange={(e) => setLead({ ...lead, message: e.target.value })}
                    placeholder="How can we help? (optional)"
                    rows={2}
                    className="w-full resize-none rounded-lg border border-[#1a1f1b]/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#c79a3e]"
                  />
                  {leadErr && <div className="text-xs text-red-600" data-testid="handoff-error">{leadErr}</div>}
                  <button
                    type="submit"
                    data-testid="handoff-submit"
                    className="w-full rounded-full bg-[#e85d04] px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-[#b34700]"
                  >
                    Send to our team
                  </button>
                </form>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-[#1a1f1b]/10 bg-[#f4f1eb] px-3 py-3">
              <div className="flex items-end gap-2">
                <textarea
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Type your message…"
                  rows={1}
                  className="max-h-24 flex-1 resize-none rounded-2xl border border-[#1a1f1b]/15 bg-white px-4 py-2.5 text-sm outline-none focus:border-[#c79a3e]"
                />
                <button
                  onClick={() => send()}
                  disabled={loading || !input.trim()}
                  data-testid="chat-send"
                  aria-label="Send"
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#1d231e] text-[#f4f1eb] transition-opacity disabled:opacity-40"
                >
                  <Send size={17} />
                </button>
              </div>
              <div className="mt-2 flex items-center justify-center gap-1.5 text-[10px] text-[#6b7268]">
                <ShieldCheck size={11} className="text-[#2f9e44]" /> Not medical advice · consult a professional for health concerns
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
